<script lang="ts" setup>
import type { ContentLayout } from '~/types/layouts'
import { bulletPointSvgImages } from '~/models/images'
import type { LayoutButton } from '~/types/elementsUi'
import type { RouteLocationRaw } from 'vue-router'
import type { TailwindColor } from '#shared/types/elementsUi'

interface ListSectionProps {
  items?: string | string[] | ContentLayout[]
  title?: string
  description?: string
  backgroundColor?: TailwindColor
  buttons?: LayoutButton[]
}

const props = withDefaults(defineProps<ListSectionProps>(), {
  backgroundColor: undefined,
  title: 'components.home-cook-benefits.title',
  items: 'components.home-cook-benefits.items',
  buttons: undefined
})

const { t, tm, te } = useI18n()

const computedItems = computed<ContentLayout[] | string[]>(() => {
  if (Array.isArray(props.items) && typeof props.items[0] === 'string') {
    return props.items
  }
  return typeof props.items === 'string' ? tm(props.items) : props.items
})
</script>

<template>
  <VSection :title :ui="{ section: backgroundColor && `bg-${backgroundColor}` }">
    <template #content>
      <p v-if="description" class="mb-4">
        {{ te(description) ? t(description) : description }}
      </p>

      <ol class="vegetable-list columns multiline text-left">
        <li
          :style="{
            '--list-item-image': `url('${bulletPointSvgImages[itemIndex]}')`
          }"
          v-for="(item, itemIndex) in computedItems"
          :key="`item-${itemIndex}`"
          class="column half"
        >
          <div class="pl-2">
            <strong v-if="typeof item === 'string'">{{ item }}</strong>
            <template v-else>
              <strong>{{ item.title }}</strong>
              <p>{{ item.content }}</p>
            </template>
          </div>
        </li>
      </ol>
    </template>

    <template #after>
      <slot name="after">
        <div
          v-for="(button, buttonIndex) in buttons"
          :key="`button-${buttonIndex}`"
          class="section-button"
        >
          <VButton
            :to="
              button.type === 'internal'
                ? ({ name: button.url } as RouteLocationRaw)
                : button.url
            "
            :label="button.label"
            @click="button.click && button.click()"
          />
        </div>
      </slot>
    </template>
  </VSection>
</template>

<style scoped></style>
